import { DEFAULT_PAGE_SIZE, Dataset, DtFilter, DtSort } from '@/components/data-table/helpers';
import { AdminUserModel, CreateAdminUserModel } from '../models/admin-user.model';
import { CreateResponse, DeleteResponse, PatchResponse } from '.';
import { JsonObject } from '../helpers';
import { create, deleteApi, get, patch, post } from './sp-api.service';
import { AppDataQueryKey } from '../query-client';
import { clearAppData } from '../app-data';

type ListResponse = {
  data: JsonObject[];
  count: number;
};

const SAFE_LIMIT = 200;

export type AdminUserPermissions = {
  canViewConversations: boolean;
};

export const getAdminUserPermData = async (): Promise<AdminUserPermissions> => {
  const { conversationAccess = false } = (await get('/user/admin-users/conversation-access')) as {
    conversationAccess: boolean;
  };

  return {
    canViewConversations: conversationAccess,
  };
};

export const getAdminUsers = async (
  page = 0,
  pageSize = DEFAULT_PAGE_SIZE,
  dtSort?: DtSort,
  dtFilter?: DtFilter
): Promise<Dataset<AdminUserModel>> => {
  const response = (await post('/user/admin-users/search', {
    page: 1,
    pageSize: SAFE_LIMIT,
    count: false,
    sort: [dtSort ? { [dtSort.columnName]: dtSort.direction } : { name: 'asc' }],
  })) as ListResponse;

  if (!response?.data) {
    return {
      page,
      pageSize,
      rows: [],
      total: 0,
    };
  }

  let rows: AdminUserModel[] = response.data.map((data) => new AdminUserModel(data));

  if (dtFilter) {
    const { values, keyword } = dtFilter;

    if (keyword) {
      const safeKeyword = String(keyword).toLowerCase();
      rows = rows.filter(
        ({ name, email }) =>
          name.toLowerCase().includes(safeKeyword) || email.toLowerCase().includes(safeKeyword)
      );
    }

    if (values?.length) {
      values.forEach(({ columnName, value }) => {
        switch (columnName) {
          case 'role':
            rows = rows.filter(({ role }) => role === value);
            break;
        }
      });
    }
  }

  const total = rows.length;

  // Paginate
  const startIndex = page * pageSize;
  rows = rows.slice(startIndex, startIndex + pageSize);

  return {
    page,
    pageSize,
    rows,
    total,
  };
};

export const getAdminUserById = async (userId: string): Promise<AdminUserModel | null> => {
  const response = await get(`/user/admin-users/${userId}`);
  return response ? new AdminUserModel(response as JsonObject) : null;
};

export const patchAdminUser = async (adminUser: AdminUserModel): Promise<PatchResponse> => {
  // cannot change last owner role to non-owner
  if (adminUser.role !== 'org:owner') {
    const existUser = await getAdminUserById(adminUser.id);
    if (existUser?.role === 'org:owner') {
      const preventPatch = await isLastOwner();
      if (preventPatch) {
        return { patched: false, error: 'You cannot change the role of the last owner' };
      }
    }
  }

  const response = await patch(`/user/admin-users/${adminUser.id}`, adminUser._props);

  // reload permissions for the signed-in admin user
  if (response.patched) {
    clearAppData(AppDataQueryKey.AdminUserPermissions);
  }

  return response;
};

export const createAdminUser = async (user: AdminUserModel): Promise<CreateResponse> => {
  const createUser = new CreateAdminUserModel(user);
  const response = await create(`/user/admin-users`, createUser._props);

  if (!response.created) {
    return response;
  }

  response.model = new AdminUserModel(response.data);

  return response;
};

export const deleteAdminUser = async (user: AdminUserModel): Promise<DeleteResponse> => {
  const { id: userId, role } = user;
  if (role === 'org:owner') {
    // cannot delete last owner
    const preventDelete = await isLastOwner();
    if (preventDelete) {
      return { deleted: false, error: 'You cannot delete the last owner' };
    }
  }
  return deleteApi(`/user/admin-users/${userId}`);
};

export const isLastOwner = async (): Promise<boolean> => {
  const ownerCount = await getOwnerCount();
  return ownerCount < 2;
};

// @todo perf just get a count, don't return documents
const getOwnerCount = async (): Promise<number> => {
  const response = await getAdminUsers(0, DEFAULT_PAGE_SIZE, undefined, {
    keyword: '',
    fields: [],
    values: [{ columnName: 'role', value: 'org:owner' }],
  });

  return response?.rows?.length || 0;
};
